import moment, { Moment } from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';

import { Avatar, Popup } from '..';
import { ROUTES } from '../../AppRouting';
import { noAnalyticsData } from '../../pages/Analytics/icons';
import {
  GetAnalyticsData,
  setNotEnoughData,
} from '../../store/analytics/analytics.actions';
import { signOut } from '../../store/auth/auth.actions';
import { RootState } from '../../store/store';
import CustomSelect from '../CustomSelect/CustomSelect';
import { PopupOption } from '../Popup/PopupOption';
import { getCurrentPath } from '../utils';
import { AccountIcon } from './icons/AccountIcon';
import { SignOutIcon } from './icons/SignOutIcon';

export const Header: React.FC = (): JSX.Element => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { stats, measurements, noData } = useSelector(
    (state: RootState) => state.analytics
  );

  const { pathname } = useLocation();
  const currentRoute = ROUTES.find(
    ({ path }) => getCurrentPath(path, user) === pathname
  );
  const history = useHistory();
  const dispatch = useDispatch();

  const onAccountClick = () => {
    history.push(`/orgs/${user?.orgId}/users/${user?.userId}`);
  };

  const onSignOutClick = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh');
    dispatch(signOut());
  };

  const options: JSX.Element[] = [
    <PopupOption
      text="Account"
      onChoose={onAccountClick}
      className="header__user-popup__option popup__option"
      Icon={AccountIcon}
      disabled={currentRoute?.label === 'Account'}
    />,
    <PopupOption
      text="Sign out"
      onChoose={onSignOutClick}
      className="header__user-popup__option popup__option"
      Icon={SignOutIcon}
    />,
  ];

  const [open, setOpen] = useState(false);
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const avatar = useRef<HTMLDivElement>(null);

  const handleClickOutside = (e: MouseEvent) => {
    const target = e.target as Node;
    const isAvatarTarget = avatar.current?.contains(target);
    if (!isAvatarTarget && open) setOpen(false);
    else if (isAvatarTarget) setOpen(!open);
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () =>
      document.removeEventListener('click', handleClickOutside, true);
  });

  const [dateArray, setDateArray] = useState<any>();

  function generateArrayOfMonthes(start: Moment, count: number) {
    const startMoment = moment(start);
    const arrResults = []; // eslint-disable-next-line
    for (let i = 0; i < count; i++) {
      const temp = moment(startMoment).subtract(i, 'month');
      const startOfMonth = `${temp.format('YYYY-MM')}-01`;
      const endOfMonth = moment(startOfMonth, 'YYYY-MM-DD')
        .add(1, 'month')
        .subtract(1, 'day')
        .format('YYYY-MM-DD');
      arrResults.push({
        label: temp.format('MMM YYYY'),
        start: startOfMonth,
        end: endOfMonth,
      });
    }
    return setDateArray(arrResults);
  }

  const [curMonth, setCurMonth] = useState<typeof dateArray>();

  const conditionToShowWarningNoData =
    noData && currentRoute?.label === 'Personal Q';

  const warnMessage = conditionToShowWarningNoData
    ? `Not enough data for ${curMonth ? curMonth.label : 'current month'}`
    : '';

  const loadDataByChoosedMonth = (arr: typeof dateArray) => {
    const fromDate = arr.start;
    const toDate = arr.end;
    setCurMonth(arr);
    setTooltipIsOpen(false);
    if (user) {
      dispatch(GetAnalyticsData(user.orgId, user.userId, fromDate, toDate));
    }
  };

  useEffect(() => {
    generateArrayOfMonthes(moment(), 12);
  }, []);

  useEffect(() => {
    if (dateArray && currentRoute && currentRoute?.label !== 'Personal Q')
      setCurMonth(dateArray[0]);
  }, [currentRoute]);

  const headerTitle = () => {
    let title;
    if (currentRoute?.label === 'Account') title = 'Account Settings';
    else if (currentRoute?.label === 'Personal Q') title = 'Your Personal Q';
    else title = currentRoute?.label;
    return title;
  };

  return (
    <header className="header">
      <h1 className="header__title">{headerTitle()}</h1>

      {currentRoute?.label === 'Personal Q' && dateArray?.length ? (
        <div className="header__analytics-block">
          <div className="header__analytics-block__select-button-block">
            <CustomSelect
              items={dateArray}
              icon={conditionToShowWarningNoData ? noAnalyticsData : null}
              selectedItem={dateArray[0]}
              onChange={loadDataByChoosedMonth}
              disabled={stats === null}
              isData={noData}
              style={{ border: '0.1px solid $border-color' }}
            />
            <button
              className="header__analytics-block__select-button-block__downloadButton"
              disabled
            >
              PDF
            </button>
          </div>
          <div className="header__analytics-block__warn-message">
            {warnMessage}
          </div>
        </div>
      ) : null}

      <div className="header__user">
        <span className="header__user-name">{`${user?.firstName || ''} ${
          user?.lastName || ''
        }`}</span>
        <div ref={avatar}>
          <Avatar
            user={user}
            image={user?.avatar}
            className="header__user-avatar"
          />
        </div>
        <Popup open={open} className="header__user-popup" options={options} />
      </div>
    </header>
  );
};
